/**
 * Custom toast transition for react-toastify v9.
 * Enter: @starting-style + data-toast-mounted fallback in app-toast.module.css.
 * Exit: data-ending-style driven CSS transitions (transitionend-aware).
 */
import { ReactElement, useEffect, useLayoutEffect, useRef } from 'react';
import { ToastTransitionProps } from 'react-toastify';

const TOAST_EXIT_FALLBACK_MS = 350;
const EXIT_DONE_PROPERTIES = new Set(['transform', 'opacity']);

export function toastTransition() {
    return function ToastTransition({
        children,
        done,
        nodeRef,
        isIn,
        preventExitTransition,
    }: ToastTransitionProps) {
        const doneRef = useRef(done);

        doneRef.current = done;

        useLayoutEffect(() => {
            if (!isIn) {
                return undefined;
            }

            const node = nodeRef.current;

            if (!node) {
                return undefined;
            }

            delete node.dataset.endingStyle;
            node.dataset.toastMounted = 'false';

            const frameId = window.requestAnimationFrame(() => {
                node.dataset.toastMounted = 'true';
            });

            return () => {
                window.cancelAnimationFrame(frameId);
            };
        }, [isIn, nodeRef]);

        useEffect(() => {
            if (isIn) {
                return undefined;
            }

            if (preventExitTransition) {
                doneRef.current();
                return undefined;
            }

            const node = nodeRef.current;

            if (!node) {
                doneRef.current();
                return undefined;
            }

            let hasFinished = false;
            let fallbackId = 0;
            let startFrameId = 0;
            let handleExitEnd: (event: TransitionEvent) => void = () => undefined;

            const completeExit = () => {
                if (hasFinished) {
                    return;
                }

                hasFinished = true;
                node.removeEventListener('transitionend', handleExitEnd);
                window.clearTimeout(fallbackId);
                delete node.dataset.endingStyle;
                doneRef.current();
            };

            startFrameId = window.requestAnimationFrame(() => {
                node.dataset.endingStyle = 'true';
                // Force reflow so the ending-style transition starts from the current frame.
                node.getBoundingClientRect();

                handleExitEnd = (event: TransitionEvent) => {
                    if (event.target !== node) {
                        return;
                    }

                    if (!EXIT_DONE_PROPERTIES.has(event.propertyName)) {
                        return;
                    }

                    completeExit();
                };

                node.addEventListener('transitionend', handleExitEnd);
                fallbackId = window.setTimeout(completeExit, TOAST_EXIT_FALLBACK_MS);
            });

            return () => {
                window.cancelAnimationFrame(startFrameId);
                window.clearTimeout(fallbackId);
                node.removeEventListener('transitionend', handleExitEnd);

                if (!hasFinished) {
                    completeExit();
                }
            };
        }, [isIn, nodeRef, preventExitTransition]);

        return children as ReactElement;
    };
}
